import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, FunnelChart, Funnel, LabelList, ReferenceLine } from 'recharts';
import { toast } from 'sonner';
import { TrendingUp, DollarSign, PieChart as PieChartIcon, Calendar, Trophy, Users, Award, ShieldAlert, Filter, X, ArrowDownRight, Clock, Percent, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DatePickerField } from '../components/DatePickerField';
import { HelpTooltip } from '../components/HelpTooltip';
import { MissingCompletionBanner } from '../components/MissingCompletionBanner';
import { useApiCacheRefresh } from '../lib/apiCache';
import { RejectionsTab } from '../components/reports/RejectionsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = ['#FF6B35', '#F59E0B', '#10B981'];

const MONTHS = [
  { value: 1, label: 'Styczeń' },
  { value: 2, label: 'Luty' },
  { value: 3, label: 'Marzec' },
  { value: 4, label: 'Kwiecień' },
  { value: 5, label: 'Maj' },
  { value: 6, label: 'Czerwiec' },
  { value: 7, label: 'Lipiec' },
  { value: 8, label: 'Sierpień' },
  { value: 9, label: 'Wrzesień' },
  { value: 10, label: 'Październik' },
  { value: 11, label: 'Listopad' },
  { value: 12, label: 'Grudzień' }
];

export const Reports = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // RBAC Guard
  if (user?.role === 'montaz' || user?.role === 'duch') {
    const isDemo = user?.role === 'duch';
    return (
      <Layout>
        <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-8 text-center max-w-md">
            <ShieldAlert className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[var(--crm-text-primary)] mb-2">Brak dostępu</h2>
            <p className="text-[var(--crm-text-secondary)] mb-4">
              {isDemo 
                ? 'Raporty są niedostępne w trybie Demo.'
                : 'Raporty są dostępne tylko dla administratorów i asystentów biura.'
              }
            </p>
            <Button 
              onClick={() => navigate(isDemo ? '/projects' : '/montaz')}
              className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white"
            >
              {isDemo ? 'Przeglądaj zlecenia' : 'Przejdź do Montażu'}
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  return <ReportsContent />;
};

const ReportsContent = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Filter state
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const isFiltered = selectedUserId !== 'all' || selectedMonth !== 'all';

  // Fetch users for the dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API_URL}/users`);
        setUsersList(response.data);
      } catch (err) { console.error("Error:", err); }
    };
    fetchUsers();
  }, []);

  const fetchData = useCallback(async (silent) => {
    try {
      if (silent !== true) setLoading(true);
      const params = {};
      if (selectedMonth !== 'all') params.month = parseInt(selectedMonth);
      if (selectedMonth !== 'all') params.year = parseInt(selectedYear);
      if (selectedUserId !== 'all') params.user_id = selectedUserId;

      const hasDateFilter = selectedMonth !== 'all';

      const requests = [
        axios.get(`${API_URL}/reports/stats`, { params }),
        axios.get(`${API_URL}/projects`, { params: selectedUserId !== 'all' ? {} : {} })
      ];

      const canSeeUserStats = user?.role === 'admin' || user?.role === 'asystent_biura';
      if (canSeeUserStats) {
        requests.push(axios.get(`${API_URL}/reports/user-stats`, { params }));
      }

      const responses = await Promise.all(requests);
      setStats(responses[0].data);

      // Scope projects to own for Handlowiec B2B (even if they have leads_all for the inbox)
      let baseProjects = responses[1].data;
      if (user?.role === 'handlowiec_b2b') {
        const myId = user.id;
        const myInit = (user.initials || '').toUpperCase();
        baseProjects = baseProjects.filter(p =>
          p.owner_id === myId ||
          (p.created_by || '').toUpperCase() === myInit ||
          (p.owner_initials || '').toUpperCase() === myInit
        );
      }

      // Filter projects locally based on current filters
      let filteredProjects = baseProjects;
      if (selectedUserId !== 'all') {
        filteredProjects = filteredProjects.filter(p => p.owner_id === selectedUserId);
      }
      if (hasDateFilter) {
        const m = parseInt(selectedMonth);
        const y = parseInt(selectedYear);
        filteredProjects = filteredProjects.filter(p => {
          const d = new Date(p.created_at);
          return d.getMonth() + 1 === m && d.getFullYear() === y;
        });
      }
      setProjects(filteredProjects);
      setAllProjects(baseProjects);

      if (!isFiltered) {
        calculateMonthlyTrend(baseProjects);
      }

      if (canSeeUserStats && responses[2]) {
        setUserStats(responses[2].data);
      }
    } catch (err) { console.error("Fetch error:", err);
      toast.error('Błąd pobierania danych');
    } finally {
      setLoading(false);
    }
  }, [selectedUserId, selectedMonth, selectedYear, user?.role, user?.id, user?.initials, isFiltered]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useApiCacheRefresh(() => fetchData(true));

  const calculateMonthlyTrend = (projectsData) => {
    const currentYear = new Date().getFullYear();
    const trend = MONTHS.map(month => {
      const monthProjects = projectsData.filter(p => {
        const date = new Date(p.created_at);
        return date.getMonth() + 1 === month.value && date.getFullYear() === currentYear;
      });
      return {
        name: month.label.substring(0, 3),
        month: month.value,
        przychód: monthProjects.reduce((sum, p) => sum + p.total_offer, 0),
        marża: monthProjects.reduce((sum, p) => sum + p.margin, 0),
        projekty: monthProjects.length
      };
    });
    setMonthlyTrend(trend);
  };

  const clearFilters = () => {
    setSelectedUserId('all');
    setSelectedMonth('all');
    setSelectedYear(new Date().getFullYear().toString());
  };

  const getMonthName = (val) => MONTHS.find(m => m.value === parseInt(val))?.label || '';

  const getFilterLabel = () => {
    const parts = [];
    if (selectedUserId !== 'all') {
      const u = usersList.find(u => u.id === selectedUserId);
      parts.push(u ? `${u.initials} (${u.email.split('@')[0]})` : 'Pracownik');
    }
    if (selectedMonth !== 'all') {
      parts.push(`${getMonthName(selectedMonth)} ${selectedYear}`);
    }
    return parts.length > 0 ? parts.join(' / ') : 'Wszystkie dane';
  };

  if (loading && !stats) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-[var(--crm-text-primary)]">Ładowanie...</div>
        </div>
      </Layout>
    );
  }

  const marginData = projects.slice(0, 10).map(p => ({
    name: p.nazwa.length > 15 ? p.nazwa.substring(0, 15) + '...' : p.nazwa,
    margin: p.margin,
    marginPercent: p.margin_percent
  }));

  const statusData = stats ? [
    { name: 'Do Realizacji', value: stats.projects_by_status?.do_realizacji || 0 },
    { name: 'W Toku', value: stats.projects_by_status?.w_toku || 0 },
    { name: 'Zakończony', value: stats.projects_by_status?.zakończony || 0 }
  ] : [];

  const canSeeUserStats = user?.role === 'admin' || user?.role === 'asystent_biura';

  const years = [];
  for (let y = 2024; y <= new Date().getFullYear() + 1; y++) years.push(y);

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight">Raporty</h1>
          <p className="text-[var(--crm-text-secondary)] mt-1 text-sm">
            {isFiltered ? `Filtr: ${getFilterLabel()}` : 'Analiza rentowności i statystyki projektów'}
          </p>
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1 mb-6 bg-[var(--crm-bg-panel)] p-1 rounded-lg w-fit" data-testid="reports-tabs">
          {[
            { id: 'overview', label: 'Przegląd' },
            { id: 'goals', label: 'Wyniki i cele' },
            { id: 'funnel', label: 'Lejek sprzedażowy' },
            { id: 'ranking', label: 'Ranking handlowców' },
            { id: 'rejections', label: 'Powody odrzuceń' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              data-testid={`reports-tab-${t.id}`}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === t.id ? 'bg-[#FF6B35] text-white' : 'text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-card-hover)]'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (<>
        {/* Filter Toolbar */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 mb-6" data-testid="reports-filter-toolbar">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-[var(--crm-text-secondary)] shrink-0" />

            {/* User Filter */}
            <div className="flex-1 min-w-[180px] max-w-[240px]">
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-9" data-testid="filter-user-select">
                  <SelectValue placeholder="Pracownik" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                  <SelectItem value="all" className="text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-btn-secondary)]">
                    Wszyscy użytkownicy
                  </SelectItem>
                  {usersList.filter(u => u.role !== 'duch').map(u => (
                    <SelectItem key={u.id} value={u.id} className="text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-btn-secondary)]">
                      <span className="flex items-center gap-2">
                        <span className="bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-primary)] text-[10px] px-1.5 py-0.5 rounded font-bold">{u.initials}</span>
                        {u.email.split('@')[0]}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Month Filter */}
            <div className="min-w-[140px] max-w-[160px]">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-9" data-testid="filter-month-select">
                  <SelectValue placeholder="Miesiąc" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                  <SelectItem value="all" className="text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-btn-secondary)]">Wszystkie miesiące</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()} className="text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-btn-secondary)]">
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Year Filter */}
            <div className="min-w-[90px] max-w-[100px]">
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-9" data-testid="filter-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()} className="text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-btn-secondary)]">{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Clear filters */}
            {isFiltered && (
              <Button
                onClick={clearFilters}
                variant="ghost"
                size="sm"
                className="text-[#FF6B35] hover:text-[var(--crm-text-primary)] hover:bg-[#FF6B35] h-9 px-3"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Wyczyść
              </Button>
            )}
          </div>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6">
          <Card data-testid="report-total-margin" className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-emerald-500/10 rounded-lg flex items-center justify-center shrink-0">
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="min-w-0">
                <p className="text-[var(--crm-text-secondary)] text-xs">
                  {isFiltered ? 'Marża (filtr)' : 'Łączna Marża'}
                </p>
                <p className="text-xl md:text-2xl font-manrope font-bold text-[var(--crm-text-primary)] truncate">
                  {stats?.total_margin?.toLocaleString('pl-PL', { minimumFractionDigits: 2 }) || '0,00'} zł
                </p>
              </div>
            </div>
          </Card>

          <Card data-testid="report-avg-margin" className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-amber-500/10 rounded-lg flex items-center justify-center shrink-0">
                <TrendingUp className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-[var(--crm-text-secondary)] text-xs">Średnia Marża</p>
                <p className="text-xl md:text-2xl font-manrope font-bold text-[var(--crm-text-primary)]">
                  {stats?.avg_margin_percent?.toFixed(2) || '0.00'}%
                </p>
              </div>
            </div>
          </Card>

          <Card data-testid="report-total-projects" className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center shrink-0">
                <PieChartIcon className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <div>
                <p className="text-[var(--crm-text-secondary)] text-xs">
                  {isFiltered ? 'Projekty (filtr)' : 'Całkowita Liczba'}
                </p>
                <p className="text-xl md:text-2xl font-manrope font-bold text-[var(--crm-text-primary)]">
                  {stats?.total_projects || 0} projektów
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Monthly Trend Chart - Only show when no month filter */}
        {selectedMonth === 'all' && (
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 mb-6">
            <h2 className="text-lg font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">
              Trend Miesięczny {selectedYear}
              <span className="text-[var(--crm-text-secondary)] text-xs font-normal ml-2">(kliknij słupek aby filtrować)</span>
            </h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart 
                data={monthlyTrend}
                onClick={(data) => {
                  if (data?.activePayload) {
                    setSelectedMonth(data.activePayload[0].payload.month.toString());
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#334E78" />
                <XAxis dataKey="name" stroke="#94A3B8" />
                <YAxis stroke="#94A3B8" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#243b5e', border: '1px solid #334E78', borderRadius: '8px' }}
                  labelStyle={{ color: '#F8FAFC' }}
                  formatter={(value, name) => [
                    `${value.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł`,
                    name === 'przychód' ? 'Przychód' : 'Marża'
                  ]}
                />
                <Legend 
                  formatter={(value) => value === 'przychód' ? 'Przychód' : 'Marża'}
                  wrapperStyle={{ color: '#F8FAFC' }}
                />
                <Bar dataKey="przychód" fill="#FF6B35" radius={[4, 4, 0, 0]} cursor="pointer" />
                <Bar dataKey="marża" fill="#10B981" radius={[4, 4, 0, 0]} cursor="pointer" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6">
            <h2 className="text-lg font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">
              {isFiltered ? 'Top Projekty (filtr)' : 'Top 10 Projektów - Marża'}
            </h2>
            {marginData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={marginData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334E78" />
                  <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#94A3B8" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#243b5e', border: '1px solid #334E78', borderRadius: '8px' }}
                    labelStyle={{ color: '#F8FAFC' }}
                    formatter={(value) => [`${value.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł`, 'Marża']}
                  />
                  <Bar dataKey="margin" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-[var(--crm-text-secondary)]">
                Brak projektów w wybranym okresie
              </div>
            )}
          </Card>

          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6">
            <h2 className="text-lg font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">
              {isFiltered ? 'Status (filtr)' : 'Projekty wg Statusu'}
            </h2>
            {statusData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" labelLine={false}
                    label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                    outerRadius={90} dataKey="value"
                  >
                    {statusData.map((_, i) => <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#243b5e', border: '1px solid #334E78', borderRadius: '8px' }} />
                  <Legend wrapperStyle={{ color: '#F8FAFC' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-[var(--crm-text-secondary)]">
                Brak projektów w wybranym okresie
              </div>
            )}
          </Card>
        </div>

        {/* User Stats - Ranking */}
        {canSeeUserStats && userStats && (
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 mt-6">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
              <h2 className="text-lg font-manrope font-semibold text-[var(--crm-text-primary)] flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#FF6B35]" />
                Ranking Użytkowników
                {isFiltered && <span className="text-[var(--crm-text-secondary)] text-xs font-normal">({getFilterLabel()})</span>}
              </h2>
              <div className="flex items-center gap-2 text-[var(--crm-text-secondary)]">
                <Users className="w-4 h-4" />
                <span className="text-xs">
                  Marża firmy: 
                  <span className="text-emerald-400 font-bold ml-1">
                    {userStats.total_company_margin.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                  </span>
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {userStats.users.map((userStat, index) => {
                const isTop = index < 3;
                const medalColors = ['text-yellow-400', 'text-[var(--crm-text-secondary)]', 'text-amber-600'];
                const sharePercent = userStats.total_company_margin > 0 
                  ? ((userStat.total_margin / userStats.total_company_margin) * 100).toFixed(1)
                  : 0;
                
                return (
                  <div 
                    key={userStat.user_id}
                    data-testid={`user-stat-${userStat.user_id}`}
                    className={`p-4 rounded-lg border transition-all ${
                      isTop 
                        ? 'bg-gradient-to-br from-[#142036] to-[#1a2c4d] border-[#FF6B35]/50' 
                        : 'bg-[var(--crm-bg-panel)] border-[var(--crm-border)]'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isTop ? 'bg-[#FF6B35]' : 'bg-[var(--crm-bg-btn-secondary)]'
                        }`}>
                          <span className="text-[var(--crm-text-primary)] font-bold">{userStat.initials}</span>
                        </div>
                        <div>
                          <p className="text-[var(--crm-text-primary)] font-medium text-sm">{userStat.email.split('@')[0]}</p>
                          <p className="text-[var(--crm-text-muted)] text-xs">{userStat.role === 'admin' ? 'Administrator' : 'Użytkownik'}</p>
                        </div>
                      </div>
                      {isTop ? (
                        <div className={`flex items-center gap-1 ${medalColors[index]}`}>
                          <Award className="w-5 h-5" />
                          <span className="font-bold">#{index + 1}</span>
                        </div>
                      ) : (
                        <span className="text-[var(--crm-text-muted)] text-sm">#{index + 1}</span>
                      )}
                    </div>
                    
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span className="text-[var(--crm-text-secondary)] text-xs">Marża:</span>
                        <span className={`font-mono font-bold text-sm ${userStat.total_margin > 0 ? 'text-emerald-400' : 'text-[var(--crm-text-secondary)]'}`}>
                          {userStat.total_margin.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[var(--crm-text-secondary)] text-xs">Udział:</span>
                        <span className="text-[#FF6B35] font-bold text-sm">{sharePercent}%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[var(--crm-text-secondary)] text-xs">Projekty:</span>
                        <span className="text-[var(--crm-text-primary)] text-sm">
                          {userStat.total_projects} 
                          <span className="text-[var(--crm-text-muted)] text-xs ml-1">({userStat.completed_projects} zak.)</span>
                        </span>
                      </div>
                      <div className="mt-2 h-1.5 bg-[var(--crm-bg-btn-secondary)] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#FF6B35] to-emerald-500 transition-all duration-500"
                          style={{ width: `${Math.min(sharePercent, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {userStats.users.length === 0 && (
              <div className="text-center py-8 text-[var(--crm-text-secondary)]">
                Brak danych w wybranym okresie
              </div>
            )}
          </Card>
        )}

        {/* Projects Table */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 mt-6">
          <h2 className="text-lg font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">
            {isFiltered ? `Projekty (${getFilterLabel()})` : 'Wszystkie Projekty - Marża'}
          </h2>
          <div className="overflow-x-auto">
            {projects.length > 0 ? (
              <Table className="w-full">
                <TableHeader>
                  <TableRow className="border-b border-[var(--crm-border)]">
                    <TableHead className="text-left py-3 px-4 text-[var(--crm-text-secondary)] font-semibold">Nazwa</TableHead>
                    <TableHead className="text-left py-3 px-4 text-[var(--crm-text-secondary)] font-semibold">Klient</TableHead>
                    <TableHead className="text-left py-3 px-4 text-[var(--crm-text-secondary)] font-semibold">Data</TableHead>
                    <TableHead className="text-right py-3 px-4 text-[var(--crm-text-secondary)] font-semibold">Oferta</TableHead>
                    <TableHead className="text-right py-3 px-4 text-[var(--crm-text-secondary)] font-semibold">Koszty</TableHead>
                    <TableHead className="text-right py-3 px-4 text-[var(--crm-text-secondary)] font-semibold">Marża</TableHead>
                    <TableHead className="text-right py-3 px-4 text-[var(--crm-text-secondary)] font-semibold">Marża %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((project) => {
                    const dodatkoweSum = project.costs.dodatkowe 
                      ? project.costs.dodatkowe.reduce((sum, item) => sum + item.kwota, 0) : 0;
                    const totalCosts = project.costs.okna + project.costs.drzwi + project.costs.montaz + dodatkoweSum;
                    return (
                      <TableRow key={project.id} className="border-b border-[var(--crm-border)] hover:bg-[var(--crm-bg-card-hover)]">
                        <TableCell className="py-3 px-4 text-[var(--crm-text-primary)] text-sm">{project.nazwa}</TableCell>
                        <TableCell className="py-3 px-4 text-[var(--crm-text-secondary)] text-sm">{project.klient}</TableCell>
                        <TableCell className="py-3 px-4 text-[var(--crm-text-secondary)] text-sm">
                          {new Date(project.created_at).toLocaleDateString('pl-PL')}
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right text-[var(--crm-text-secondary)] font-mono text-sm">
                          {project.total_offer.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right text-[var(--crm-text-secondary)] font-mono text-sm">
                          {totalCosts.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right font-mono text-sm">
                          <span className={project.margin >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                            {project.margin.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                          </span>
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right font-mono text-sm">
                          <span className={project.margin_percent >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                            {project.margin_percent.toFixed(2)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow className="bg-[var(--crm-bg-panel)]">
                    <TableCell colSpan="3" className="py-3 px-4 text-[var(--crm-text-primary)] font-semibold">SUMA</TableCell>
                    <TableCell className="py-3 px-4 text-right text-[var(--crm-text-primary)] font-mono font-semibold">
                      {projects.reduce((sum, p) => sum + p.total_offer, 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                    </TableCell>
                    <TableCell className="py-3 px-4 text-right text-[var(--crm-text-primary)] font-mono font-semibold">
                      {projects.reduce((sum, p) => {
                        const d = p.costs.dodatkowe ? p.costs.dodatkowe.reduce((s, i) => s + i.kwota, 0) : 0;
                        return sum + p.costs.okna + p.costs.drzwi + p.costs.montaz + d;
                      }, 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                    </TableCell>
                    <TableCell className="py-3 px-4 text-right text-emerald-400 font-mono font-semibold">
                      {projects.reduce((sum, p) => sum + p.margin, 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                    </TableCell>
                    <TableCell className="py-3 px-4 text-right text-emerald-400 font-mono font-semibold">
                      {projects.length > 0 
                        ? (projects.reduce((sum, p) => sum + p.margin_percent, 0) / projects.length).toFixed(2)
                        : '0.00'
                      }%
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            ) : (
              <div className="text-center py-8 text-[var(--crm-text-secondary)]">
                Brak projektów w wybranym okresie
              </div>
            )}
          </div>
        </Card>
        </>)}

        {activeTab === 'funnel' && <FunnelTab usersList={usersList} />}
        {activeTab === 'ranking' && <RankingTab />}
        {activeTab === 'goals' && <GoalsTab />}
        {activeTab === 'rejections' && <RejectionsTab usersList={usersList} canFilterUsers={user?.role === 'admin' || user?.role === 'asystent_biura'} />}
      </div>
    </Layout>
  );
};


const FUNNEL_COLORS = ['#3B82F6', '#8B5CF6', '#06B6D4', '#F59E0B', '#FF6B35', '#10B981'];

const FunnelTab = ({ usersList }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userId, setUserId] = useState('all');

  const fetchFunnel = useCallback(async (silent) => {
    if (silent !== true) setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (userId !== 'all') params.user_id = userId;
      const res = await axios.get(`${API_URL}/reports/funnel`, { params });
      setData(res.data);
    } catch (err) { console.error("Fetch error:", err);
      toast.error('Błąd pobierania danych lejka');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, userId]);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);
  useApiCacheRefresh(() => fetchFunnel(true));

  if (loading && !data) return <div className="text-center text-[var(--crm-text-secondary)] py-12">Ładowanie lejka...</div>;
  if (!data) return null;

  const funnelChartData = data.stages.map((s, i) => ({
    name: s.label,
    value: s.count,
    fill: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
  }));

  const maxCount = Math.max(...data.stages.map(s => s.count), 1);

  return (
    <div className="space-y-6" data-testid="funnel-tab">
      {/* Filters */}
      <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
        <div className="flex flex-wrap items-end gap-4">
          <Filter className="w-4 h-4 text-[var(--crm-text-secondary)] shrink-0 mb-2" />
          <div>
            <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Od</label>
            <DatePickerField value={dateFrom} onChange={setDateFrom} placeholder="Data od" />
          </div>
          <div>
            <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Do</label>
            <DatePickerField value={dateTo} onChange={setDateTo} placeholder="Data do" />
          </div>
          <div className="min-w-[180px]">
            <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Handlowiec</label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-9" data-testid="funnel-user-filter">
                <SelectValue placeholder="Wszyscy" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                <SelectItem value="all" className="text-[var(--crm-text-primary)]">Wszyscy</SelectItem>
                {usersList.filter(u => u.role !== 'montaz' && u.role !== 'duch').map(u => (
                  <SelectItem key={u.id} value={u.id} className="text-[var(--crm-text-primary)]">{u.initials} ({u.email.split('@')[0]})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(dateFrom || dateTo || userId !== 'all') && (
            <Button size="sm" variant="ghost" onClick={() => { setDateFrom(''); setDateTo(''); setUserId('all'); }}
              className="text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] mb-0.5">
              <X className="w-4 h-4 mr-1" />Wyczyść
            </Button>
          )}
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 text-center">
          <ArrowDownRight className="w-5 h-5 text-blue-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-[var(--crm-text-primary)]">{data.total_projects}</p>
          <p className="text-[var(--crm-text-secondary)] text-xs">Projektów w lejku</p>
        </Card>
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 text-center">
          <Percent className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-emerald-400">{data.conversion_rate}%</p>
          <p className="text-[var(--crm-text-secondary)] text-xs">Konwersja Lead → Zakończono</p>
        </Card>
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 text-center">
          <Clock className="w-5 h-5 text-amber-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-[var(--crm-text-primary)]">
            {(() => {
              const allDays = data.stages.filter(s => s.avg_days !== null).map(s => s.avg_days);
              return allDays.length ? (allDays.reduce((a, b) => a + b, 0)).toFixed(0) : '—';
            })()}
          </p>
          <p className="text-[var(--crm-text-secondary)] text-xs">Śr. dni cały cykl</p>
        </Card>
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 text-center">
          <X className="w-5 h-5 text-red-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-red-400">{data.rejected_count}</p>
          <p className="text-[var(--crm-text-secondary)] text-xs">Odrzucone</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel Visualization */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6" data-testid="funnel-chart-card">
          <h3 className="text-lg font-semibold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
            <ArrowDownRight className="w-5 h-5 text-[#FF6B35]" />
            Lejek sprzedażowy
            <HelpTooltip text="Wizualizacja procesu sprzedaży od leada do zakończenia — ilość projektów na każdym etapie" />
          </h3>
          {data.total_projects === 0 ? (
            <p className="text-[var(--crm-text-secondary)] text-center py-8">Brak danych w wybranym zakresie</p>
          ) : (
            <div className="space-y-2">
              {data.stages.map((s, i) => {
                const widthPct = Math.max((s.count / maxCount) * 100, 8);
                return (
                  <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-stage-${s.key}`}>
                    <div className="w-28 text-right text-xs text-[var(--crm-text-secondary)] shrink-0 truncate">{s.label}</div>
                    <div className="flex-1 relative">
                      <div
                        className="h-9 rounded-md flex items-center px-3 transition-all duration-500"
                        style={{ width: `${widthPct}%`, backgroundColor: FUNNEL_COLORS[i], opacity: 0.85 }}
                      >
                        <span className="text-[var(--crm-text-primary)] text-sm font-bold whitespace-nowrap">{s.count}</span>
                      </div>
                    </div>
                    <div className="w-14 text-right text-sm font-mono shrink-0" style={{ color: FUNNEL_COLORS[i] }}>
                      {s.percentage}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Stage Details Table */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6" data-testid="funnel-details-card">
          <h3 className="text-lg font-semibold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#FF6B35]" />
            Czas w etapach
          </h3>
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[var(--crm-border)] hover:bg-transparent">
                <TableHead className="text-[var(--crm-text-secondary)]">Etap</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] text-center">Projekty</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] text-center">%</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] text-right">Śr. dni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.stages.map((s, i) => (
                <TableRow key={s.key} className="border-b border-[var(--crm-border)]/50 hover:bg-[var(--crm-bg-card-hover)]">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: FUNNEL_COLORS[i] }} />
                      <span className="text-[var(--crm-text-primary)] text-sm">{s.label}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center text-[var(--crm-text-secondary)] font-mono">{s.count}</TableCell>
                  <TableCell className="text-center text-[var(--crm-text-secondary)] font-mono">{s.percentage}%</TableCell>
                  <TableCell className="text-right">
                    {s.avg_days !== null ? (
                      <span className={`font-mono text-sm ${s.avg_days > 7 ? 'text-red-400' : s.avg_days > 3 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {s.avg_days}d
                      </span>
                    ) : (
                      <span className="text-[var(--crm-text-muted)]">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Conversion funnel summary */}
          <div className="mt-4 pt-4 border-t border-[var(--crm-border)]">
            <div className="flex items-center justify-between">
              <span className="text-[var(--crm-text-secondary)] text-sm">Konwersja Lead → Zakończono</span>
              <span className={`text-lg font-bold ${data.conversion_rate >= 50 ? 'text-emerald-400' : data.conversion_rate >= 20 ? 'text-amber-400' : 'text-red-400'}`}>
                {data.conversion_rate}%
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};


const RankingTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortKey, setSortKey] = useState('completed_projects');
  const [sortDir, setSortDir] = useState('desc');

  const fetchRanking = useCallback(async (silent) => {
    if (silent !== true) setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API_URL}/reports/ranking`, { params });
      setData(res.data);
    } catch { toast.error('Błąd pobierania rankingu'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchRanking(); }, [fetchRanking]);
  useApiCacheRefresh(() => fetchRanking(true));

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = [...data].sort((a, b) => {
    const av = a[sortKey] ?? -Infinity;
    const bv = b[sortKey] ?? -Infinity;
    return sortDir === 'desc' ? (bv > av ? 1 : -1) : (av > bv ? 1 : -1);
  });

  const SortHeader = ({ label, colKey, className = '' }) => (
    <TableHead
      className={`text-[var(--crm-text-secondary)] cursor-pointer select-none hover:text-[var(--crm-text-primary)] transition-colors ${className}`}
      onClick={() => toggleSort(colKey)}>
      <span className="flex items-center gap-1">
        {label}
        {sortKey === colKey && <span className="text-[#FF6B35]">{sortDir === 'desc' ? '↓' : '↑'}</span>}
      </span>
    </TableHead>
  );

  return (
    <div className="space-y-6" data-testid="ranking-tab">
      {/* Filters */}
      <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
        <div className="flex flex-wrap items-end gap-4">
          <Filter className="w-4 h-4 text-[var(--crm-text-secondary)] shrink-0 mb-2" />
          <div>
            <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Od</label>
            <DatePickerField value={dateFrom} onChange={setDateFrom} placeholder="Data od" />
          </div>
          <div>
            <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Do</label>
            <DatePickerField value={dateTo} onChange={setDateTo} placeholder="Data do" />
          </div>
          {(dateFrom || dateTo) && (
            <Button size="sm" variant="ghost" onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] mb-0.5">
              <X className="w-4 h-4 mr-1" />Wyczyść
            </Button>
          )}
        </div>
      </Card>

      {loading ? (
        <div className="text-center text-[var(--crm-text-secondary)] py-12">Ładowanie rankingu...</div>
      ) : data.length === 0 ? (
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-12 text-center">
          <Users className="w-10 h-10 text-[var(--crm-text-muted)] mx-auto mb-3" />
          <p className="text-[var(--crm-text-secondary)]">Brak danych w wybranym zakresie</p>
        </Card>
      ) : (
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] overflow-hidden" data-testid="ranking-table-card">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[var(--crm-border)] hover:bg-transparent">
                  <TableHead className="text-[var(--crm-text-secondary)] w-10">#</TableHead>
                  <SortHeader label="Handlowiec" colKey="initials" />
                  <SortHeader label="Zakończone" colKey="completed_projects" className="text-center" />
                  <SortHeader label="Wszystkie" colKey="total_projects" className="text-center" />
                  <SortHeader label="Marża netto" colKey="total_net_margin" className="text-right" />
                  <SortHeader label="Śr. marża %" colKey="avg_margin_percent" className="text-right" />
                  <SortHeader label="Śr. dni" colKey="avg_completion_days" className="text-right" />
                  <SortHeader label="Konwersja" colKey="conversion_rate" className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((r, idx) => (
                  <TableRow key={r.user_id} className="border-b border-[var(--crm-border)]/50 hover:bg-[var(--crm-bg-card-hover)]"
                    data-testid={`ranking-row-${r.user_id}`}>
                    <TableCell>
                      {idx === 0 && sorted.length > 1
                        ? <Trophy className="w-4 h-4 text-amber-400" />
                        : <span className="text-[var(--crm-text-muted)] text-sm">{idx + 1}</span>
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#FF6B35]/20 flex items-center justify-center text-[#FF6B35] text-xs font-bold">
                          {r.initials}
                        </div>
                        <div>
                          <p className="text-[var(--crm-text-primary)] text-sm font-medium">{r.name}</p>
                          <p className="text-[var(--crm-text-muted)] text-xs">{r.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-emerald-400 font-bold">{r.completed_projects}</span>
                    </TableCell>
                    <TableCell className="text-center text-[var(--crm-text-secondary)]">{r.total_projects}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono font-medium ${r.total_net_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {r.total_net_margin.toLocaleString('pl-PL')} zł
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono ${r.avg_margin_percent >= 30 ? 'text-emerald-400' : r.avg_margin_percent >= 15 ? 'text-amber-400' : 'text-red-400'}`}>
                        {r.avg_margin_percent}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {r.avg_completion_days !== null ? (
                        <span className={`font-mono ${r.avg_completion_days <= 14 ? 'text-emerald-400' : r.avg_completion_days <= 30 ? 'text-amber-400' : 'text-red-400'}`}>
                          {r.avg_completion_days}d
                        </span>
                      ) : <span className="text-[var(--crm-text-muted)]">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono font-medium ${r.conversion_rate >= 50 ? 'text-emerald-400' : r.conversion_rate >= 25 ? 'text-amber-400' : 'text-red-400'}`}>
                        {r.conversion_rate}%
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
};

// ==================== WYNIKI I CELE (per użytkownik) ====================

const BASE_LABEL = { net_margin: 'marży netto', total_offer: 'wartości ofert' };
const fmtPLN = (v) => `${(v ?? 0).toLocaleString('pl-PL')} zł`;
const pctBarColor = (p) => (p >= 100 ? 'bg-emerald-500' : p >= 50 ? 'bg-amber-500' : 'bg-red-500');
const pctTextColor = (p) => (p >= 100 ? 'text-emerald-400' : p >= 50 ? 'text-amber-400' : 'text-red-400');

const DualProgress = ({ realizedPct, forecastPct }) => {
  const r = Math.max(0, Math.min(realizedPct ?? 0, 100));
  const f = Math.max(0, Math.min(forecastPct ?? 0, 100));
  const extra = Math.max(0, f - r);
  return (
    <div className="w-full h-2.5 rounded-full bg-[var(--crm-bg-panel)] overflow-hidden flex" title={`Zrealizowane: ${realizedPct ?? 0}% • Z prognozą: ${forecastPct ?? 0}%`}>
      <div className={`${pctBarColor(realizedPct ?? 0)} h-full transition-all`} style={{ width: `${r}%` }} />
      <div className="h-full opacity-60" style={{ width: `${extra}%`, backgroundImage: 'repeating-linear-gradient(45deg, var(--crm-text-muted) 0, var(--crm-text-muted) 3px, transparent 3px, transparent 6px)' }} />
    </div>
  );
};

const GoalsLegend = () => (
  <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--crm-text-secondary)]">
    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> Zrealizowane (twarde)</span>
    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundImage: 'repeating-linear-gradient(45deg, var(--crm-text-muted) 0, var(--crm-text-muted) 3px, transparent 3px, transparent 6px)' }} /> W toku (prognoza)</span>
  </div>
);

const GoalsTab = () => {
  const { user } = useAuth();
  const [mode, setMode] = useState('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('realized');
  const [sortDir, setSortDir] = useState('desc');
  const [detailUser, setDetailUser] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchGoals = useCallback(async (silent) => {
    if (silent !== true) setLoading(true);
    try {
      const params = { period: mode };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API_URL}/reports/goals`, { params });
      setData(res.data);
    } catch { toast.error('Błąd pobierania wyników i celów'); }
    finally { setLoading(false); }
  }, [mode, dateFrom, dateTo]);

  useEffect(() => { fetchGoals(); }, [fetchGoals]);
  useApiCacheRefresh(() => fetchGoals(true));

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { period: mode };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API_URL}/reports/goals/export`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `wyniki_i_cele_${data?.date_from || ''}_${data?.date_to || ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Wyeksportowano do Excela');
    } catch { toast.error('Błąd eksportu do Excela'); }
    finally { setExporting(false); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const rows = data?.rows || [];
  const sorted = [...rows].sort((a, b) => {
    if (sortKey === 'initials') {
      return sortDir === 'desc' ? b.initials.localeCompare(a.initials) : a.initials.localeCompare(b.initials);
    }
    const av = a[sortKey] ?? -Infinity;
    const bv = b[sortKey] ?? -Infinity;
    return sortDir === 'desc' ? (bv > av ? 1 : -1) : (av > bv ? 1 : -1);
  });

  const SortHeader = ({ label, colKey, className = '' }) => (
    <TableHead className={`text-[var(--crm-text-secondary)] cursor-pointer select-none hover:text-[var(--crm-text-primary)] whitespace-nowrap ${className}`} onClick={() => toggleSort(colKey)}>
      <span className="flex items-center gap-1">{label}{sortKey === colKey && <span className="text-[#FF6B35]">{sortDir === 'desc' ? '↓' : '↑'}</span>}</span>
    </TableHead>
  );

  if (detailUser) {
    return <GoalDetail userId={detailUser} mode={mode} dateFrom={dateFrom} dateTo={dateTo} onBack={() => setDetailUser(null)} />;
  }

  const summary = data?.summary;

  return (
    <div className="space-y-6" data-testid="goals-tab">
      {/* Controls */}
      <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Tryb</label>
            <div className="flex gap-1 bg-[var(--crm-bg-panel)] p-1 rounded-md">
              {[{ id: 'month', l: 'Miesiąc' }, { id: 'quarter', l: 'Kwartał' }].map((m) => (
                <button key={m.id} data-testid={`goals-mode-${m.id}`} onClick={() => setMode(m.id)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${mode === m.id ? 'bg-[#FF6B35] text-white' : 'text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]'}`}>
                  {m.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Od</label>
            <DatePickerField value={dateFrom} onChange={setDateFrom} placeholder="Data od" data-testid="goals-date-from" />
          </div>
          <div>
            <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Do</label>
            <DatePickerField value={dateTo} onChange={setDateTo} placeholder="Data do" data-testid="goals-date-to" />
          </div>
          {(dateFrom || dateTo) && (
            <Button size="sm" variant="ghost" onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] mb-0.5">
              <X className="w-4 h-4 mr-1" />Wyczyść
            </Button>
          )}
          {data && (
            <div className="ml-auto flex items-center gap-3">
              <span className="text-xs text-[var(--crm-text-muted)]">Okres: {data.date_from} → {data.date_to}</span>
              <Button size="sm" onClick={handleExport} disabled={exporting || !rows.length} data-testid="goals-export-excel-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Download className="w-4 h-4 mr-1.5" />{exporting ? 'Eksportowanie...' : 'Eksport do Excela'}
              </Button>
            </div>
          )}
        </div>
        <div className="mt-3 pt-3 border-t border-[var(--crm-border)]"><GoalsLegend /></div>
      </Card>

      {data?.is_admin_view && user?.role === 'admin' && <MissingCompletionBanner onChanged={() => fetchGoals(true)} />}

      {loading ? (
        <div className="text-center text-[var(--crm-text-secondary)] py-12">Ładowanie...</div>
      ) : rows.length === 0 ? (
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-12 text-center">
          <Users className="w-10 h-10 text-[var(--crm-text-muted)] mx-auto mb-3" />
          <p className="text-[var(--crm-text-secondary)]">Brak danych w wybranym okresie</p>
        </Card>
      ) : (
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] overflow-hidden" data-testid="goals-table-card">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[var(--crm-border)] hover:bg-transparent">
                  <SortHeader label="Użytkownik" colKey="initials" />
                  <SortHeader label="Plan" colKey="plan" className="text-right" />
                  <SortHeader label="Zrealizowane" colKey="realized" className="text-right" />
                  <SortHeader label="% realizacji planu" colKey="percent" className="min-w-[180px]" />
                  <SortHeader label="W toku (prognoza)" colKey="in_progress" className="text-right" />
                  <SortHeader label="Prognoza razem" colKey="forecast_total" className="text-right" />
                  <SortHeader label="Prowizja naliczona" colKey="commission" className="text-right" />
                  <SortHeader label="Prowizja potencjalna" colKey="commission_potential" className="text-right" />
                  <SortHeader label="Leady" colKey="leads_count" className="text-center" />
                  <SortHeader label="Aktywne" colKey="active_count" className="text-center" />
                  <SortHeader label="Zakończone" colKey="completed_count" className="text-center" />
                  <TableHead className="text-[var(--crm-text-secondary)] whitespace-nowrap text-right cursor-pointer select-none" onClick={() => toggleSort('conversion')} data-testid="goals-effectiveness-header">
                    <span className="flex items-center justify-end gap-1">Skuteczność<HelpTooltip text="Ile z rozstrzygniętych w tym okresie spraw zakończyło się sprzedażą" />{sortKey === 'conversion' && <span className="text-[#FF6B35]">{sortDir === 'desc' ? '↓' : '↑'}</span>}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((r) => (
                  <TableRow key={r.user_id} data-testid={`goals-row-${r.user_id}`}
                    onClick={() => setDetailUser(r.user_id)}
                    className="border-b border-[var(--crm-border)]/50 hover:bg-[var(--crm-bg-card-hover)] cursor-pointer">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#FF6B35]/20 flex items-center justify-center text-[#FF6B35] text-xs font-bold shrink-0">{r.initials}</div>
                        <div>
                          <p className="text-[var(--crm-text-primary)] text-sm font-medium">{r.initials}</p>
                          <p className="text-[var(--crm-text-muted)] text-[10px]">od {BASE_LABEL[r.commission_base]}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-[var(--crm-text-secondary)] whitespace-nowrap">{r.plan > 0 ? fmtPLN(r.plan) : '—'}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-400 font-medium whitespace-nowrap">{fmtPLN(r.realized)}</TableCell>
                    <TableCell>
                      {r.percent === null ? (
                        <span className="text-[var(--crm-text-muted)] text-xs italic">brak planu</span>
                      ) : (
                        <div className="space-y-1 min-w-[160px]">
                          <div className="flex items-center justify-between text-xs">
                            <span className={`font-mono font-bold ${pctTextColor(r.percent)}`}>{r.percent}%</span>
                            {r.percent_forecast !== null && <span className="text-[var(--crm-text-muted)]">z prognozą {r.percent_forecast}%</span>}
                          </div>
                          <DualProgress realizedPct={r.percent} forecastPct={r.percent_forecast} />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-[var(--crm-text-secondary)] whitespace-nowrap">{fmtPLN(r.in_progress)}</TableCell>
                    <TableCell className="text-right font-mono text-[var(--crm-text-primary)] whitespace-nowrap">
                      {fmtPLN(r.forecast_total)}
                      {r.percent_forecast !== null && <span className="block text-[10px] text-[var(--crm-text-muted)]">{r.percent_forecast}% planu</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono text-emerald-400 whitespace-nowrap">{fmtPLN(r.commission)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-400 whitespace-nowrap">{fmtPLN(r.commission_potential)}</TableCell>
                    <TableCell className="text-center text-[var(--crm-text-secondary)]">{r.leads_count}</TableCell>
                    <TableCell className="text-center text-[var(--crm-text-secondary)]">{r.active_count}</TableCell>
                    <TableCell className="text-center text-emerald-400 font-bold">{r.completed_count}</TableCell>
                    <TableCell className="text-right font-mono text-[var(--crm-text-secondary)]" data-testid={`goals-effectiveness-${r.user_id}`}>{r.conversion === null ? '-' : `${r.conversion}%`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {summary && (
                <TableFooter>
                  <TableRow className="border-t-2 border-[var(--crm-border)] bg-[var(--crm-bg-panel)]/50 hover:bg-transparent">
                    <TableCell className="font-bold text-[var(--crm-text-primary)]">Suma / średnia</TableCell>
                    <TableCell className="text-right font-mono font-bold text-[var(--crm-text-primary)]">{fmtPLN(summary.total_plan)}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-emerald-400">{fmtPLN(summary.total_realized)}</TableCell>
                    <TableCell className={`font-mono font-bold ${summary.avg_percent === null ? 'text-[var(--crm-text-muted)]' : pctTextColor(summary.avg_percent)}`}>
                      {summary.avg_percent === null ? '—' : `śr. ${summary.avg_percent}%`}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-[var(--crm-text-secondary)]">{fmtPLN(summary.total_in_progress)}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-[var(--crm-text-primary)]">{fmtPLN(summary.total_forecast)}</TableCell>
                    <TableCell colSpan={6}></TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
};

const GoalCard = ({ title, plan, realized, inProgress, percent, testid }) => (
  <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4" data-testid={testid}>
    <p className="text-[var(--crm-text-secondary)] text-xs mb-1">{title}</p>
    <div className="flex items-baseline gap-2 mb-2">
      <span className="text-2xl font-bold text-[var(--crm-text-primary)] font-mono">{fmtPLN(realized)}</span>
      <span className="text-xs text-[var(--crm-text-muted)]">/ {plan > 0 ? fmtPLN(plan) : 'brak planu'}</span>
    </div>
    {percent === null ? (
      <span className="text-[var(--crm-text-muted)] text-xs italic">brak planu</span>
    ) : (
      <>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className={`font-mono font-bold ${pctTextColor(percent)}`}>{percent}%</span>
          <span className="text-[var(--crm-text-muted)]">w toku: {fmtPLN(inProgress)}</span>
        </div>
        <DualProgress realizedPct={percent} forecastPct={plan > 0 ? Math.round((realized + inProgress) / plan * 100) : 0} />
      </>
    )}
  </Card>
);

const ProjectMiniTable = ({ projects, hideMargin, showCompleted, baseLabel, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <Table>
      <TableHeader>
        <TableRow className="border-b border-[var(--crm-border)] hover:bg-transparent">
          <TableHead className="text-[var(--crm-text-secondary)]">Nazwa</TableHead>
          <TableHead className="text-[var(--crm-text-secondary)]">Klient</TableHead>
          <TableHead className="text-[var(--crm-text-secondary)]">Status</TableHead>
          {showCompleted && <TableHead className="text-[var(--crm-text-secondary)]">Zakończono</TableHead>}
          <TableHead className="text-[var(--crm-text-secondary)] text-right">Wartość oferty</TableHead>
          {!hideMargin && <TableHead className="text-[var(--crm-text-secondary)] text-right">Marża netto</TableHead>}
          <TableHead className="text-[var(--crm-text-secondary)] text-right">Do planu ({baseLabel})</TableHead>
          <TableHead className="text-[var(--crm-text-secondary)] text-right">Prowizja</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {projects.length === 0 ? (
          <TableRow><TableCell colSpan={8} className="text-center text-[var(--crm-text-muted)] py-6">Brak projektów</TableCell></TableRow>
        ) : projects.map((p) => (
          <TableRow key={p.id} className="border-b border-[var(--crm-border)]/50 hover:bg-[var(--crm-bg-card-hover)]">
            <TableCell className="text-[var(--crm-text-primary)] text-sm">{p.nazwa}</TableCell>
            <TableCell className="text-[var(--crm-text-secondary)] text-sm">{p.klient}</TableCell>
            <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-[var(--crm-bg-panel)] text-[var(--crm-text-secondary)]">{p.status_label}</span></TableCell>
            {showCompleted && <TableCell className="text-[var(--crm-text-secondary)] text-sm font-mono" data-testid={`goal-project-completed-${p.id}`}>{p.completed_at || '—'}</TableCell>}
            <TableCell className="text-right font-mono text-[var(--crm-text-secondary)]">{fmtPLN(p.total_offer)}</TableCell>
            {!hideMargin && <TableCell className={`text-right font-mono ${p.net_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmtPLN(p.net_margin)}</TableCell>}
            <TableCell className="text-right font-mono text-[var(--crm-text-primary)]">{fmtPLN(p.plan_value)}</TableCell>
            <TableCell className="text-right font-mono text-amber-400">{fmtPLN(p.commission)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const GoalDetail = ({ userId, mode, dateFrom, dateTo, onBack }) => {
  const { user } = useAuth();
  const hideMargin = user?.role === 'handlowiec_b2b';
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetail = useCallback(async (silent) => {
    if (silent !== true) setLoading(true);
    try {
      const params = { period: mode };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API_URL}/reports/goals/${userId}`, { params });
      setD(res.data);
    } catch { toast.error('Błąd pobierania szczegółów'); }
    finally { setLoading(false); }
  }, [userId, mode, dateFrom, dateTo]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);
  useApiCacheRefresh(() => fetchDetail(true));

  if (loading) return <div className="text-center text-[var(--crm-text-secondary)] py-12">Ładowanie...</div>;
  if (!d) return null;

  return (
    <div className="space-y-6" data-testid="goal-detail">
      <div className="flex items-center gap-3">
        <Button size="sm" variant="ghost" onClick={onBack} className="text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]" data-testid="goal-detail-back">
          <ArrowDownRight className="w-4 h-4 mr-1 rotate-180" />Powrót do tabeli
        </Button>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#FF6B35]/20 flex items-center justify-center text-[#FF6B35] text-sm font-bold">{d.initials}</div>
          <div>
            <p className="text-[var(--crm-text-primary)] font-semibold">{d.initials}</p>
            <p className="text-[var(--crm-text-muted)] text-xs">{d.email}</p>
          </div>
        </div>
        <span className="ml-auto text-xs px-3 py-1.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] font-medium" data-testid="goal-base-label">
          Plan liczony od: {BASE_LABEL[d.commission_base]}
        </span>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GoalCard title="Plan miesięczny" plan={d.monthly_plan} realized={d.monthly_realized} inProgress={d.monthly_in_progress} percent={d.monthly_percent} testid="goal-card-monthly" />
        <GoalCard title="Plan kwartalny" plan={d.quarterly_plan} realized={d.quarterly_realized} inProgress={d.quarterly_in_progress} percent={d.quarterly_percent} testid="goal-card-quarterly" />
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4" data-testid="goal-card-commission">
          <p className="text-[var(--crm-text-secondary)] text-xs mb-1">Prowizja w okresie ({d.commission_percent}%)</p>
          <div className="mb-2">
            <span className="text-2xl font-bold text-emerald-400 font-mono">{fmtPLN(d.commission_period)}</span>
            <span className="block text-[10px] text-[var(--crm-text-muted)]">naliczona (od zrealizowanych)</span>
          </div>
          <div>
            <span className="text-lg font-bold text-amber-400 font-mono">{fmtPLN(d.commission_potential_period)}</span>
            <span className="block text-[10px] text-[var(--crm-text-muted)]">potencjalna (od w toku)</span>
          </div>
        </Card>
      </div>

      {/* 6-month chart */}
      <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
        <h3 className="text-[var(--crm-text-primary)] font-semibold mb-1">Realizacja vs plan (6 miesięcy)</h3>
        <p className="text-[var(--crm-text-muted)] text-xs mb-4">Baza: {BASE_LABEL[d.commission_base]} • linia = plan miesięczny</p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={d.history}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--crm-border)" />
            <XAxis dataKey="label" stroke="var(--crm-text-secondary)" fontSize={12} />
            <YAxis stroke="var(--crm-text-secondary)" fontSize={12} domain={[0, (dataMax) => Math.ceil(Math.max(dataMax, d.monthly_plan) * 1.1)]} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v, n) => [fmtPLN(v), n === 'realized' ? 'Zrealizowane' : 'W toku']} contentStyle={{ backgroundColor: 'var(--crm-bg-dialog)', border: '1px solid var(--crm-border)', borderRadius: 8, color: 'var(--crm-text-primary)' }} />
            <Legend formatter={(v) => (v === 'realized' ? 'Zrealizowane' : 'W toku (prognoza)')} />
            <Bar dataKey="realized" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} />
            <Bar dataKey="in_progress" stackId="a" fill="#94A3B8" fillOpacity={0.55} radius={[4, 4, 0, 0]} />
            {d.monthly_plan > 0 && <ReferenceLine y={d.monthly_plan} stroke="#FF6B35" strokeDasharray="5 4" label={{ value: 'Plan', fill: '#FF6B35', fontSize: 11, position: 'right' }} />}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Project lists */}
      <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
        <h3 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">Zakończone w okresie <span className="text-xs text-[var(--crm-text-muted)] font-normal">({fmtPLN(d.realized_period)})</span></h3>
        <ProjectMiniTable projects={d.realized_projects} hideMargin={hideMargin} showCompleted baseLabel={BASE_LABEL[d.commission_base]} testid="goal-realized-projects" />
      </Card>
      <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
        <h3 className="text-[var(--crm-text-secondary)] font-semibold mb-3 flex items-center gap-2">W toku (pipeline) <span className="text-xs text-[var(--crm-text-muted)] font-normal">(prognoza {fmtPLN(d.in_progress_period)})</span></h3>
        <ProjectMiniTable projects={d.pipeline_projects} hideMargin={hideMargin} baseLabel={BASE_LABEL[d.commission_base]} testid="goal-pipeline-projects" />
      </Card>
    </div>
  );
};

